// The company's voice switches (BUILD 2 C2-3, WP2-4 step 8), mounted on the
// Company profile page for the owner: the voice-pass switch, the signatures
// switch (off by default; off purges), unused-days, the audit list and the
// enrolled count only.
//
// WP2-0b slot: renders nothing until WP2-4 fills it.
import { useEffect, useRef, useState } from 'react';
import { useAppCatalogLocale } from '@/i18n/context';
import { useNamespaceCopy } from '@/i18n/ns';
import voiceSignatureCopy from '@/i18n/ns/voice-signature';
import { useCompany } from './context';
import { voiceApi, type CompanyVoiceSettingsResponse, type VoiceAuditPage } from './voice-api';
import { signatureError } from '@/voice/signature-error';

export function CompanyVoiceSection() {
  const copy = useNamespaceCopy(voiceSignatureCopy);
  const locale = useAppCatalogLocale();
  const company = useCompany();
  const number = new Intl.NumberFormat(locale);
  const [data, setData] = useState<CompanyVoiceSettingsResponse | null>(null);
  const [audit, setAudit] = useState<VoiceAuditPage | null>(null);
  const [busy, setBusy] = useState(false);
  const [days, setDays] = useState('365');
  useEffect(() => {
    if (data) setDays(String(data.settings.unusedRetentionDays));
  }, [data]);
  const [message, setMessage] = useState<string | null>(null);
  const request = useRef(0);
  const busyRef = useRef(false);
  const busyToken = useRef(0);
  const scopeKey = `${company.data?.company?.id ?? ''}:${company.data?.membership?.userId ?? ''}`;
  const owner =
    company.data?.membership?.role === 'owner' &&
    company.data.membership.permissions?.manageVoiceSignatures === true;
  const load = async (key: string, token: number) => {
    try {
      const [settings, events] = await Promise.all([voiceApi.settings(), voiceApi.audit()]);
      if (token === request.current && key === scopeKey) {
        setData(settings);
        setAudit(events);
      }
    } catch (error) {
      if (token === request.current && key === scopeKey) setMessage(signatureError(error, copy));
    }
  };
  useEffect(() => {
    const token = ++request.current;
    setData(null);
    setAudit(null);
    setMessage(null);
    busyRef.current = false;
    ++busyToken.current;
    setBusy(false);
    if (owner && scopeKey !== ':') void load(scopeKey, token);
    return () => {
      request.current += 1;
    };
  }, [owner, scopeKey]);
  if (!owner) return null;
  if (!data)
    return (
      <section className="company-form">
        <p role="status">{message ?? copy.loading}</p>
      </section>
    );
  const save = async (patch: Parameters<typeof voiceApi.putSettings>[0]) => {
    if (busyRef.current) return;
    const key = scopeKey;
    const token = request.current;
    const operation = ++busyToken.current;
    busyRef.current = true;
    setBusy(true);
    setMessage(null);
    try {
      const next = await voiceApi.putSettings(patch);
      const events = await voiceApi.audit();
      if (token === request.current && key === scopeKey) {
        setData(next);
        setAudit(events);
      }
    } catch (error) {
      if (token === request.current && key === scopeKey) setMessage(signatureError(error, copy));
    } finally {
      if (token === request.current && key === scopeKey) setBusy(false);
      if (busyToken.current === operation) busyRef.current = false;
    }
  };
  const loadMore = async () => {
    if (!audit?.next || busyRef.current) return;
    const key = scopeKey;
    const token = request.current;
    const operation = ++busyToken.current;
    busyRef.current = true;
    setBusy(true);
    try {
      const page = await voiceApi.audit(audit.next);
      if (token === request.current && key === scopeKey)
        setAudit({ events: [...audit.events, ...page.events], next: page.next });
    } catch (error) {
      if (token === request.current && key === scopeKey) setMessage(signatureError(error, copy));
    } finally {
      if (token === request.current && key === scopeKey) setBusy(false);
      if (busyToken.current === operation) busyRef.current = false;
    }
  };
  const settings = data.settings;
  return (
    <section className="company-form" aria-labelledby="company-voice-title">
      <h2 id="company-voice-title">{copy.orgTitle}</h2>
      <label className="company-check-row">
        <input
          type="checkbox"
          checked={settings.voicePassEnabled}
          disabled={busy}
          onChange={(e) => void save({ voicePassEnabled: e.target.checked })}
        />{' '}
        {copy.voicePassSwitch}
      </label>
      <label className="company-check-row">
        <input
          type="checkbox"
          checked={settings.signaturesEnabled}
          disabled={busy}
          onChange={async (e) => {
            if (
              !e.target.checked &&
              data.enrolledCount > 0 &&
              !window.confirm(
                copy.purgeWarning.replace('{count}', number.format(data.enrolledCount)),
              )
            )
              return;
            await save({
              signaturesEnabled: e.target.checked,
              ...(e.target.checked ? {} : { purge: true }),
            });
          }}
        />{' '}
        {copy.signaturesSwitch}
      </label>
      <label>
        {copy.unusedDays}
        <input
          type="number"
          min={30}
          max={365}
          value={days}
          disabled={busy}
          onChange={(e) => setDays(e.target.value)}
          onBlur={() => {
            const value = Number(days);
            if (!Number.isInteger(value) || value < 30 || value > 365) {
              setDays(String(settings.unusedRetentionDays));
              return;
            }
            if (value !== settings.unusedRetentionDays) void save({ unusedRetentionDays: value });
          }}
        />
      </label>
      <p>{copy.enrolledCount.replace('{count}', number.format(data.enrolledCount))}</p>
      {message && <p role="alert">{message}</p>}
      <details className="company-voice-audit">
        <summary>{copy.auditTitle}</summary>
        {audit?.events.length ? (
          <ul>
            {audit.events.map((event) => (
              <li key={event.id}>
                {event.action === 'voiceprint_deleted'
                  ? copy.deleted
                  : event.action === 'voiceprint_created'
                    ? copy.saved
                    : copy.auditTitle}{' '}
                <time dateTime={event.at}>{new Date(event.at).toLocaleDateString(locale)}</time>
              </li>
            ))}
          </ul>
        ) : (
          <p>{copy.auditTitle}</p>
        )}
        {audit?.next && (
          <button className="company-secondary" disabled={busy} onClick={() => void loadMore()}>
            {copy.auditTitle}
          </button>
        )}
      </details>
    </section>
  );
}
